import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  LoadingOutlined,
  PauseOutlined,
} from '@ant-design/icons';
import { Waypoint } from '@omniafishing/core';
import classNames from 'classnames';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useProUpsellModal } from '../../hooks/use_pro_upsell_modal';
import { useUser } from '../../hooks/use_user';
import { SignupLoginModalActions } from '../../redux/signup_login_modal';
import { PRO_TRIAL_SIGNUP_LOGIN_MODAL_OPEN_PARAMS } from '../login_signup_modal/signup_login_modal';
import SvgCloud from '../svg/cloud';
import SvgLayerPrecipitation from '../svg/layer_precipitation';
import SvgLayerWind from '../svg/layer_wind';
import SvgLockIcon from '../svg/lock_icon';
import SvgMoonrise from '../svg/moonrise';
import SvgMoonset from '../svg/moonset';
import SvgPressure from '../svg/pressure';
import SvgSunrise from '../svg/sunrise';
import SvgSunset from '../svg/sunset';
import SvgWindDirection from '../svg/wind_direction';
import styles from './current_conditions.less';
import { moonPhaseImages, pressureGroup } from './current_conditions_lib';
import { OverheadUnderfootAmount } from './overhead_underfoot_amount';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

interface CurrentConditionsProps {
  waypoint: Waypoint;
  isProBlocked: boolean;
  className?: string;
  onDataLoaded?: () => void;
  solunarLoading: boolean;
  weatherLoading: boolean;
}

export const CurrentConditions = (props: CurrentConditionsProps) => {
  const { waypoint, isProBlocked, className, weatherLoading, solunarLoading } = props;

  const {
    air_temperature_f,
    barometer_in_hg,
    barometer_trend,
    cloud_cover_percent,
    icon,
    moon_overhead_at,
    moon_percent,
    moon_phase,
    moon_underfoot_at,
    moonrise,
    moonset,
    outing_at,
    precipitation_percent,
    sunrise,
    sunset,
    timezone,
    weather_name,
    wind_direction,
    wind_mph,
  } = waypoint;

  const { isPro, user } = useUser();
  const isNotProAndIsProBlocked = !isPro && isProBlocked;
  const dispatch = useDispatch();
  const { openProUpsellModal } = useProUpsellModal();

  const onUnlockClick = () => {
    if (user) {
      openProUpsellModal();
    } else {
      dispatch(
        SignupLoginModalActions.SIGNUP_LOGIN_MODAL_OPEN(PRO_TRIAL_SIGNUP_LOGIN_MODAL_OPEN_PARAMS)
      );
    }
  };

  const formatDate = (date: string) => {
    if (!date) {
      return '-';
    }
    return dayjs(date).tz(timezone).format('h:mm A');
  };

  const UnlockPro = (
    <>
      <span className={styles.proBadge}>PRO</span>
      <span className={styles.unlock} onClick={onUnlockClick}>
        <SvgLockIcon />
        Unlock Weather
      </span>
    </>
  );

  const Loading = (
    <span className={styles.loading}>
      <LoadingOutlined />
      Loading
    </span>
  );

  return (
    <div className={classNames(styles.conditions, className)}>
      <section
        id="weather-section"
        className={classNames(styles.condition, {
          [styles.condition__frosted]: isNotProAndIsProBlocked || weatherLoading,
        })}
      >
        <div className={styles.conditionHeading}>
          <h2>Weather</h2>
          <span>{weather_name}</span>
        </div>
        <div className={styles.weatherTop}>
          <div className={styles.weatherCurrent}>
            {icon && (
              <img
                src={`/${icon.replace('.png', '')}.svg`}
                alt={weather_name && `weather icon for ${weather_name}`}
                width={74}
                height={74}
                className="overflow-hidden"
              />
            )}
            <span>{air_temperature_f != null ? <>{Math.round(air_temperature_f)}°F</> : '-'}</span>
          </div>
          <div className={styles.weatherTopCondtions}>
            <p className={styles.conditionAttr}>
              <SvgLayerWind />
              Wind{' '}
              <strong className={styles.wind}>
                {wind_mph != null ? <>{Math.round(wind_mph)} mph </> : '-'}
                {wind_direction != null && (
                  <SvgWindDirection
                    style={{
                      transform: `rotate(${wind_direction}deg)`,
                    }}
                  />
                )}
              </strong>
            </p>
            <p className={styles.conditionAttr}>
              <SvgLayerPrecipitation /> Precip{' '}
              <strong>{precipitation_percent != null ? <>{precipitation_percent}%</> : '-'}</strong>
            </p>
            <p className={styles.conditionAttr}>
              <SvgCloud />
              Cloud Cover{' '}
              <strong>{cloud_cover_percent != null ? <>{cloud_cover_percent}%</> : '-'}</strong>
            </p>
          </div>
        </div>
        <div className={styles.pressure}>
          <p className={styles.conditionAttr}>
            <SvgPressure /> Pressure{' '}
            <strong>
              {barometer_in_hg != null ? barometer_in_hg : '-'} inHg •{' '}
              {barometer_in_hg && pressureGroup(barometer_in_hg)}
            </strong>
            <span className={styles.pressureTrend}>
              {barometer_trend === 'falling' && <ArrowDownOutlined />}
              {barometer_trend === 'steady' && <PauseOutlined />}
              {barometer_trend === 'rising' && <ArrowUpOutlined />}
              {barometer_trend}
            </span>
          </p>
        </div>
        {isNotProAndIsProBlocked ? UnlockPro : weatherLoading && Loading}
      </section>

      <section
        id="solunar-section"
        className={classNames(styles.condition, {
          [styles.condition__frosted]: isNotProAndIsProBlocked || solunarLoading,
        })}
      >
        <div className={styles.conditionHeading}>
          <h2>Solunar</h2>
        </div>
        <div className={styles.solunarTop}>
          <div>
            <img src={moonPhaseImages[moon_phase]} alt={moon_phase} width={74} height={74} />
          </div>
          <div>
            <p className={styles.conditionAttr}>
              <strong>
                {moon_percent != null ? <>{Math.round(moon_percent)}%</> : ''} {moon_phase}
              </strong>
            </p>
            <p className={styles.conditionAttr}>
              <OverheadUnderfootAmount
                timestamp={outing_at}
                moonUnderfootAt={moon_underfoot_at}
                moonOverheadAt={moon_overhead_at}
              />
            </p>
          </div>
        </div>
        <div className={styles.solunarRiseSets}>
          <div className={styles.solunarRiseSetsCol}>
            <p className={styles.conditionAttr}>
              <SvgSunrise />
              Sunrise <strong>{formatDate(sunrise)}</strong>
            </p>
            <p className={styles.conditionAttr}>
              <SvgSunset />
              Sunset <strong>{formatDate(sunset)}</strong>
            </p>
          </div>
          <div className={styles.solunarRiseSetsCol}>
            <p className={styles.conditionAttr}>
              <SvgMoonrise />
              Moonrise <strong>{formatDate(moonrise)}</strong>
            </p>
            <p className={styles.conditionAttr}>
              <SvgMoonset />
              Moonset <strong>{formatDate(moonset)}</strong>
            </p>
          </div>
        </div>
        {isNotProAndIsProBlocked ? UnlockPro : solunarLoading && Loading}
      </section>
    </div>
  );
};
